import type { AuthAdapter, User } from '@cv-builder/core'
import { requestFromParent, onParentMessage, VH_MESSAGES } from './iframe'

export interface VHAuthConfig {
  /** Timeout for auth requests in ms */
  timeout?: number
  /** Allowed parent origins for security */
  allowedOrigins?: string[]
}

/**
 * Virtual Hive authentication adapter
 * Gets auth tokens and user info from the parent window via postMessage
 */
export function createVHAuthAdapter(config: VHAuthConfig = {}): AuthAdapter {
  const { timeout = 5000 } = config

  let cachedUser: User | null = null
  let cachedToken: string | null = null
  let tokenExpiry: number | null = null
  const listeners: Set<(user: User | null) => void> = new Set()

  const notifyListeners = () => {
    for (const listener of listeners) {
      listener(cachedUser)
    }
  }

  // Listen for auth updates from parent
  onParentMessage((message) => {
    if (message.type === 'AUTH_STATE_CHANGED') {
      const payload = message.payload as { user: User | null; token: string | null } | null
      cachedUser = payload?.user ?? null
      cachedToken = payload?.token ?? null
      notifyListeners()
    }
  }, config.allowedOrigins)

  return {
    isAuthenticated(): boolean {
      return cachedUser !== null
    },

    getUser(): User | null {
      return cachedUser
    },

    async getAccessToken(): Promise<string | null> {
      // Return cached token if still valid
      if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
        return cachedToken
      }

      try {
        const response = await requestFromParent<{ token: string; expiresIn?: number }>(
          VH_MESSAGES.REQUEST_AUTH_TOKEN,
          undefined,
          timeout
        )

        cachedToken = response.token
        // Cache for slightly less than expiry to be safe
        tokenExpiry = response.expiresIn
          ? Date.now() + (response.expiresIn - 30) * 1000
          : Date.now() + 5 * 60 * 1000 // Default 5 min

        return cachedToken
      } catch (error) {
        console.error('Failed to get auth token from parent:', error)
        return null
      }
    },

    async login(): Promise<void> {
      // Request parent to initiate login
      requestFromParent('REQUEST_LOGIN', undefined, timeout)
    },

    async logout(): Promise<void> {
      // Request parent to logout
      requestFromParent('REQUEST_LOGOUT', undefined, timeout)
      cachedUser = null
      cachedToken = null
      tokenExpiry = null
      notifyListeners()
    },

    onAuthStateChange(callback: (user: User | null) => void): () => void {
      listeners.add(callback)

      // Try to get initial user info
      requestFromParent<{ user: User | null }>(VH_MESSAGES.REQUEST_USER_INFO, undefined, timeout)
        .then((response) => {
          cachedUser = response.user
          callback(cachedUser)
        })
        .catch(() => {
          callback(null)
        })

      return () => {
        listeners.delete(callback)
      }
    },
  }
}
